/**
 * interchange.js — pure graph import/export mapping.
 *
 * elementsToInterchange(elements) → { vertices: [...], edges: [...] }
 *   Projects the in-memory element list to interchange JSON, keeping only the
 *   public fields (element_id, labels, props for vertices; element_id, label,
 *   from_id, to_id, props for edges). Internal fields (rev, type, timestamps, user_id)
 *   are stripped so the interchange is safe to export and re-import.
 *
 * interchangeToOperations(json) → operation[]
 *   Converts interchange JSON to apply_session_graph_mutations operation objects.
 *   Vertices come first (so edge endpoints always exist when edges are applied).
 */

/**
 * Convert an in-memory element list to the interchange format.
 *
 * @param {Array} elements - Array of element objects ({element_id, type, ...})
 * @returns {{ vertices: Array, edges: Array }}
 */
export function elementsToInterchange(elements) {
  const vertices = [];
  const edges = [];

  for (const el of elements) {
    if (el.type === 'edge') {
      edges.push({
        element_id: el.element_id,
        label: el.label || '',
        from_id: el.from_id || '',
        to_id: el.to_id || '',
        props: el.props || {},
      });
    } else {
      // Default: treat as vertex (type === 'vertex' or unspecified)
      vertices.push({
        element_id: el.element_id,
        labels: el.labels || [],
        props: el.props || {},
      });
    }
  }

  return { vertices, edges };
}

/**
 * Convert interchange JSON to a flat operation list for apply_session_graph_mutations.
 * Vertices are emitted before edges so endpoints always exist.
 *
 * @param {Object} json - Interchange object with `vertices` and `edges` arrays
 * @returns {Array} - Array of operation objects
 */
export function interchangeToOperations(json) {
  if (json === null || json === undefined) {
    throw new Error('interchangeToOperations: input must be a non-null object');
  }
  if (!Array.isArray(json.vertices)) {
    throw new Error('interchangeToOperations: missing or invalid "vertices" array in interchange JSON');
  }
  if (!Array.isArray(json.edges)) {
    throw new Error('interchangeToOperations: missing or invalid "edges" array in interchange JSON');
  }

  const ops = [];

  for (const v of json.vertices) {
    const op = { op: 'add_vertex', element_id: v.element_id };
    if (v.labels !== undefined) op.labels = v.labels;
    if (v.props !== undefined) op.props = v.props;
    ops.push(op);
  }

  for (const e of json.edges) {
    const op = {
      op: 'add_edge',
      element_id: e.element_id,
      label: e.label,
      from_id: e.from_id,
      to_id: e.to_id,
    };
    if (e.props !== undefined) op.props = e.props;
    ops.push(op);
  }

  return ops;
}
